// hooks/useDomainesCached.js
import { useCallback, useEffect, useState } from 'react';
import { useCacheManager } from './useCacheManager';

// Utiliser l'URL du User Backend depuis les variables d'environnement
const USER_BACKEND_API_BASE_URL = process.env.REACT_APP_USER_BACKEND_API_URL;

export const useDomainesCached = (options = {}) => {
    // Options par défaut
    const {
        cacheDuration = 5 * 60 * 1000, // 5 minutes
        forceRefresh = false,
        autoRefresh = true
    } = options;

    // États
    const [domaines, setDomaines] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [lastFetch, setLastFetch] = useState(null);
    const [isFromCache, setIsFromCache] = useState(false);

    // Hook de cache
    const { getCacheKey, setCacheData, getCacheData, invalidateCache, hasDataChanged } = useCacheManager();

    // Headers pour les requêtes
    const getHeaders = useCallback(() => {
        const token = localStorage.getItem('access_token');
        return {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        };
    }, []);

    // Fonction utilitaire pour gérer les erreurs
    const handleError = useCallback((error, customMessage = 'Une erreur est survenue') => {
        const message = error?.message || customMessage;
        setError(error);
        console.error('❌ Erreur domaines:', message);
        return { success: false, error: message };
    }, []);

    // 📋 RÉCUPÉRER LES DOMAINES AVEC CACHE
    const fetchDomaines = useCallback(async (customFilters = {}, useCache = true) => {
        // Préparer la clé de cache
        const filters = { actif: true, hierarchy: false, ...customFilters };
        const cacheKey = getCacheKey('domaines', filters);

        // Vérifier le cache d'abord (sauf si forceRefresh)
        if (useCache && !forceRefresh) {
            const cachedData = getCacheData(cacheKey);
            if (cachedData) {
                console.log('📦 Domaines chargés depuis le cache');
                setDomaines(cachedData);
                setIsFromCache(true);
                setError(null);
                return { success: true, domaines: cachedData, fromCache: true };
            }
        }

        setLoading(true);
        setError(null);
        setIsFromCache(false);

        try {
            // Nettoyer les filtres et créer l'URL
            const cleanFilters = {};
            Object.entries(filters).forEach(([key, value]) => {
                if (value !== null && value !== undefined && value !== 'null' && value !== '') {
                    cleanFilters[key] = typeof value === 'boolean' ? value.toString() : value;
                }
            });

            const queryParams = new URLSearchParams(cleanFilters);
            const url = `${USER_BACKEND_API_BASE_URL}/domaines?${queryParams}`;

            console.log('🌐 Requête API domaines:', url);
            const response = await fetch(url, { headers: getHeaders() });

            if (response.ok) {
                const data = await response.json();
                const fetchedDomaines = data.domaines || [];

                // Sauvegarder dans le cache
                setCacheData(cacheKey, fetchedDomaines, cacheDuration);
                
                setDomaines(fetchedDomaines);
                setLastFetch(Date.now());
                
                console.log(`✅ ${fetchedDomaines.length} domaines chargés depuis l'API`);
                return { success: true, domaines: fetchedDomaines, fromCache: false };
            } else {
                const errorData = await response.json();
                throw new Error(errorData.error || errorData.message || 'Erreur lors du chargement des domaines');
            }
        } catch (error) {
            return handleError(error, 'Erreur lors de la récupération des domaines');
        } finally {
            setLoading(false);
        }
    }, [getCacheKey, getCacheData, setCacheData, getHeaders, handleError, forceRefresh, cacheDuration]);

    // 🆕 CRÉER UN NOUVEAU DOMAINE avec invalidation du cache
    const createDomaine = useCallback(async (domaineData) => {
        setLoading(true);
        setError(null);

        try {
            if (!domaineData.nom_domaine?.trim()) {
                throw new Error('Le nom du domaine est requis');
            }

            const response = await fetch(`${USER_BACKEND_API_BASE_URL}/domaines`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify(domaineData)
            });

            if (response.ok) {
                const data = await response.json();
                
                // Invalider le cache des domaines
                invalidateCache('domaines');
                console.log('🗑️ Cache domaines invalidé après création');
                
                // Ajouter à l'état local pour un feedback immédiat
                setDomaines(prev => [...prev, data.domaine]);
                
                return { success: true, domaine: data.domaine };
            } else {
                const errorData = await response.json();
                throw new Error(errorData.error || errorData.message || 'Erreur lors de la création du domaine');
            }
        } catch (error) {
            return handleError(error, 'Erreur lors de la création du domaine');
        } finally {
            setLoading(false);
        }
    }, [getHeaders, handleError, invalidateCache]);

    // ✏️ METTRE À JOUR UN DOMAINE avec invalidation du cache
    const updateDomaine = useCallback(async (domaineId, updateData) => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${USER_BACKEND_API_BASE_URL}/domaines/${domaineId}`, {
                method: 'PUT',
                headers: getHeaders(),
                body: JSON.stringify(updateData)
            });

            if (response.ok) {
                const data = await response.json();
                
                // Invalider le cache des domaines
                invalidateCache('domaines');
                console.log('🗑️ Cache domaines invalidé après modification');
                
                // Mettre à jour l'état local
                setDomaines(prev =>
                    prev.map(domaine =>
                        domaine.id_domaine === domaineId
                            ? { ...domaine, ...data.domaine }
                            : domaine
                    )
                );
                
                return { success: true, domaine: data.domaine };
            } else {
                const errorData = await response.json();
                throw new Error(errorData.error || errorData.message || 'Erreur lors de la mise à jour du domaine');
            }
        } catch (error) {
            return handleError(error, 'Erreur lors de la mise à jour du domaine');
        } finally {
            setLoading(false);
        }
    }, [getHeaders, handleError, invalidateCache]);

    // 🗑️ SUPPRIMER UN DOMAINE avec invalidation du cache
    const deleteDomaine = useCallback(async (domaineId) => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${USER_BACKEND_API_BASE_URL}/domaines/${domaineId}`, {
                method: 'DELETE',
                headers: getHeaders()
            });

            if (response.ok) {
                const data = await response.json();
                
                // Invalider le cache des domaines
                invalidateCache('domaines');
                console.log('🗑️ Cache domaines invalidé après suppression');
                
                // Retirer de l'état local
                setDomaines(prev =>
                    prev.filter(domaine => domaine.id_domaine !== domaineId)
                );
                
                return { success: true, message: data.message };
            } else {
                const errorData = await response.json();
                throw new Error(errorData.error || errorData.message || 'Erreur lors de la suppression du domaine');
            }
        } catch (error) {
            return handleError(error, 'Erreur lors de la suppression du domaine');
        } finally {
            setLoading(false);
        }
    }, [getHeaders, handleError, invalidateCache]);

    // 🔄 RAFRAÎCHIR LES DOMAINES (force le refetch)
    const refreshDomaines = useCallback(() => {
        console.log('🔄 Force refresh des domaines');
        return fetchDomaines({}, false); // false = ne pas utiliser le cache
    }, [fetchDomaines]);

    // 🎯 REFETCH avec gestion intelligente du cache
    const refetchDomaines = useCallback(async () => {
        console.log('🔄 Refetch intelligent des domaines');
        
        // Si les données sont récentes (moins de 1 minute), ne pas refetch
        if (lastFetch && (Date.now() - lastFetch) < 60000 && !forceRefresh) {
            console.log('⏭️ Données récentes, skip refetch');
            return { success: true, domaines, fromCache: true };
        }
        
        return fetchDomaines({}, false);
    }, [fetchDomaines, lastFetch, forceRefresh, domaines]);

    // 🔄 CHARGER LES DOMAINES AU MONTAGE
    useEffect(() => {
        if (autoRefresh) {
            fetchDomaines();
        }
    }, [fetchDomaines, autoRefresh]);

    // 🧹 NETTOYER LES ERREURS
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    return {
        // États
        domaines,
        loadingDomaines: loading,
        errorDomaines: error,
        isFromCache,
        lastFetch,

        // Actions CRUD
        fetchDomaines,
        createDomaine,
        updateDomaine,
        deleteDomaine,
        refreshDomaines,
        refetchDomaines,

        // Utilitaires
        clearError,
        
        // Cache management
        invalidateCache: () => invalidateCache('domaines')
    };
};